import { useState } from "react";

const RENDERER_URL =
  import.meta.env.VITE_RENDERER_URL ?? "http://localhost:5052";

type Mode = "link" | "inline" | "button" | "trigger" | "manual";

const MODES: { id: Mode; label: string; hint: string }[] = [
  {
    id: "link",
    label: "Share a link",
    hint: "Send it, or put it in a QR code",
  },
  { id: "inline", label: "Embed in a page", hint: "Grows with the form" },
  { id: "button", label: "Floating button", hint: "Sits in a page corner" },
  { id: "trigger", label: "Open on click", hint: "Any button or link" },
  { id: "manual", label: "From JavaScript", hint: "You decide when" },
];

const snippetFor = (mode: Mode, url: string, script: string): string => {
  switch (mode) {
    case "link":
      return url;

    case "inline":
      return `<div id="koaform-embed"></div>
<script src="${script}" async></script>
<script>
  window.Koaform = window.Koaform || function () {
    (Koaform.q = Koaform.q || []).push(arguments);
  };
  Koaform("inline", {
    url: "${url}",
    selector: "#koaform-embed"
  });
</script>`;

    case "button":
      return `<script src="${script}" async></script>
<script>
  window.Koaform = window.Koaform || function () {
    (Koaform.q = Koaform.q || []).push(arguments);
  };
  Koaform("button", {
    url: "${url}",
    text: "Give feedback",
    position: "bottom-right"
  });
</script>`;

    case "trigger":
      return `<!-- Any element with data-koaform opens the form -->
<a href="${url}" data-koaform>Give feedback</a>

<script src="${script}" async></script>`;

    case "manual":
      return `<script src="${script}" async></script>
<script>
  window.Koaform = window.Koaform || function () {
    (Koaform.q = Koaform.q || []).push(arguments);
  };

  // Open it whenever you like
  Koaform("open", { url: "${url}" });

  // …and close it
  // Koaform("close");
</script>`;
  }
};

const CopyBox = ({ value }: { value: string }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard is blocked without a user gesture in some browsers, and on
      // http origins. The textarea is selectable, so this is recoverable.
      setCopied(false);
    }
  };

  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-[11px] border border-line-input bg-subtle p-4 pr-24 font-mono text-xs leading-relaxed text-body">
        <code>{value}</code>
      </pre>
      <button
        type="button"
        onClick={() => void copy()}
        className="absolute top-3 right-3 rounded-[10px] border border-line-bold bg-surface px-2.5 py-1 text-xs font-semibold text-body shadow-xs hover:bg-paper"
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

/**
 * How people actually get the form onto their site.
 *
 * Koalendar offers the same four shapes (inline, floating button, click
 * trigger, JS API), so anyone moving between the two products finds what they
 * expect.
 */
export const Share = ({ slug }: { slug: string }) => {
  const [mode, setMode] = useState<Mode>("link");
  const url = `${RENDERER_URL}/r/${slug}`;
  const script = `${RENDERER_URL}/embed.js`;

  return (
    <section className="rounded-[14px] border border-line-strong bg-surface p-6">
      <h2 className="text-base font-semibold text-ink">Share your form</h2>

      <div role="tablist" className="mt-4 flex flex-wrap gap-1.5">
        {MODES.map((item) => (
          <button
            key={item.id}
            role="tab"
            type="button"
            aria-selected={mode === item.id}
            onClick={() => setMode(item.id)}
            className={`rounded-[10px] px-3 py-1.5 text-sm font-medium ${
              mode === item.id
                ? "bg-primary-600 text-white"
                : "bg-line text-body hover:bg-line-input"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <p className="mt-3 text-sm text-muted">
        {MODES.find((m) => m.id === mode)?.hint}
      </p>

      <div className="mt-3">
        <CopyBox value={snippetFor(mode, url, script)} />
      </div>

      {mode !== "link" ? (
        <p className="mt-3 text-xs text-muted">
          Paste this anywhere in your page. The script is{" "}
          <strong>2.5&nbsp;kB</strong> and loads nothing else.
        </p>
      ) : null}
    </section>
  );
};
